/**
 * B1 — the projects list, the workspace home and the first screen an architect sees
 * (chat1.md §1). KLQ-20260819-003.
 *
 * THREE DECISIONS WORTH KNOWING BEFORE CHANGING ANYTHING HERE
 *
 * 1. **The status chips and the sort are request parameters; the ownership chips are not.**
 *    `GET /api/v1/projects` filters on `status` and orders on an allowlist, and both live in
 *    the TanStack Query key — switching a chip is a different query, not a different view of
 *    the same one. It offers no filter on `relation`, which is computed per request from the
 *    caller's memberships, so `Owned by us` / `Shared with us` narrow the pages already
 *    loaded. That asymmetry is real and is recorded in the spec's Notes; it is not a
 *    shortcut, and the status chips must never join it.
 *
 * 2. **No count is shown until every page has been loaded.** The endpoint returns no totals,
 *    only `has_more`. "7 active · 12 archived" over one loaded page of twenty-four would be a
 *    number the screen invented, so the header says nothing until `hasNextPage` is false and
 *    then counts what it is actually holding (spec, Project Context).
 *
 * 3. **The list spans workspaces and the header does not claim otherwise.** chat1.md :147
 *    wants B1 scoped to one workspace; the endpoint returns every project the caller may see
 *    across their memberships. For a single-workspace practice the two are identical, and the
 *    relation badge carries the difference. Recorded as a known tension in the spec's
 *    Approach — revisit when multi-workspace users exist.
 */

import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  EmptyState,
  InlineError,
  Screen,
  ScreenHead,
  Select,
  Skeleton,
} from '@/components';
import { useMessages } from '@/lib/i18n';
import { useInfiniteSentinel } from '@/lib/infinite';

import type { ProjectRelationFilter, ProjectSort, ProjectStatusFilter } from './api';
import { DEFAULT_SORT } from './api';
import { AppChrome } from './AppChrome';
import { NewProjectDrawer } from './NewProjectDrawer';
import { ProjectCard } from './ProjectCard';
import styles from './ProjectsPage.module.css';
import { useProjectList } from './queries';

const STATUS_FILTERS: readonly ProjectStatusFilter[] = ['all', 'active', 'archived'];
const RELATION_FILTERS: readonly ProjectRelationFilter[] = ['all', 'owned', 'shared'];
const SORTS: readonly ProjectSort[] = ['-updated_at', 'name', '-created_at'];

const STATUS_LABEL = {
  all: 'filter.all',
  active: 'filter.active',
  archived: 'filter.archived',
} as const;

const RELATION_LABEL = {
  all: 'filter.all',
  owned: 'filter.owned',
  shared: 'filter.shared',
} as const;

const SORT_LABEL = {
  '-updated_at': 'sort.lastActivity',
  name: 'sort.name',
  '-created_at': 'sort.created',
} as const;

/** The placeholder cards, keyed so the grid is a list rather than four copies of one key. */
const PLACEHOLDER_CARDS: readonly string[] = ['a', 'b', 'c', 'd'];

/** Both chip rows are the same object in two groups, so their class is written once. */
function chipClass(on: boolean): string {
  return [styles.chip, on ? styles.chipOn : ''].filter(Boolean).join(' ');
}

export function ProjectsPage() {
  const t = useMessages('projects');
  const navigate = useNavigate();

  const [status, setStatus] = useState<ProjectStatusFilter>('all');
  const [relation, setRelation] = useState<ProjectRelationFilter>('all');
  const [sort, setSort] = useState<ProjectSort>(DEFAULT_SORT);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const list = useProjectList({ status, sort });

  /** Every loaded page, flattened, then narrowed by the one filter the server does not do. */
  const projects = useMemo(() => {
    const loaded = (list.data?.pages ?? []).flatMap((page) => page.items);
    return relation === 'all' ? loaded : loaded.filter((project) => project.relation === relation);
  }, [list.data, relation]);

  /** Honest only once nothing is left unloaded. See decision 2 in the module note. */
  const counts = useMemo(() => {
    if (list.hasNextPage || list.isPending) return null;
    return {
      active: projects.filter((project) => project.status === 'active').length,
      archived: projects.filter((project) => project.status === 'archived').length,
      total: projects.length,
    };
  }, [projects, list.hasNextPage, list.isPending]);

  const filtered = status !== 'all' || relation !== 'all';

  /**
   * **A studio with no project arrives with „Vytvor prvý projekt“ focused** (`KLQ-20261023-009`)
   * — the last step of the first run is one key, not a hunt. Once per mount, at the first
   * answer: a list that empties later, or empties under a filter, never pulls focus from
   * wherever the person has put it.
   */
  const createFirst = useRef<HTMLButtonElement>(null);
  const arrivalSettled = useRef(false);
  useEffect(() => {
    if (arrivalSettled.current || list.isPending) return;
    arrivalSettled.current = true;
    if (!filtered && projects.length === 0) createFirst.current?.focus();
  }, [list.isPending, filtered, projects.length]);

  function countLine(): string | null {
    if (counts === null) return null;
    // Both numbers are only meaningful when both kinds were asked for. Under a status chip the
    // screen holds one kind by construction, and "0 archived" would be an artefact of the
    // filter rather than a fact about the workspace.
    if (status === 'all') {
      return t('page.count.split', { active: counts.active, archived: counts.archived });
    }
    return t('page.count.total', { count: counts.total });
  }

  const line = countLine();

  // **Scrolling is what advances a plain list** (owner, 2026-09-10). The button below stays as
  // the guarantee rather than the affordance: a list advanced only by scrolling cannot be
  // finished by somebody on a keyboard or a screen reader, who never brings the sentinel into
  // view because they are walking a document rather than scrolling a viewport.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  useInfiniteSentinel(sentinel, {
    hasNextPage: list.hasNextPage ?? false,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: () => {
      void list.fetchNextPage();
    },
  });

  return (
    <AppChrome crumbs={[t('page.title')]}>
      <Screen
        head={
          // Level 1: the list is the workspace's front door, so its name is the `h1`. The
          // count goes in `meta` rather than beside the title — a count is the system's
          // answer, and `ScreenHead` draws it in mono for exactly that reason (`STYLE.md` §6).
          <ScreenHead
            level={1}
            title={t('page.title')}
            meta={line ?? undefined}
            actions={
              <Button
                variant="primary"
                icon={<Plus size={16} strokeWidth={1.5} aria-hidden />}
                onClick={() => {
                  setDrawerOpen(true);
                }}
              >
                {t('action.newProject')}
              </Button>
            }
          />
        }
      >
        <div className={styles.body}>
          <div className={styles.controls}>
            <div className={styles.chipGroup} role="group" aria-label={t('filter.status')}>
              {STATUS_FILTERS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={chipClass(value === status)}
                  aria-pressed={value === status}
                  onClick={() => {
                    setStatus(value);
                  }}
                >
                  {t(STATUS_LABEL[value])}
                </button>
              ))}
            </div>

            <div className={styles.divider} />

            <div className={styles.chipGroup} role="group" aria-label={t('filter.relation')}>
              {RELATION_FILTERS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={chipClass(value === relation)}
                  aria-pressed={value === relation}
                  onClick={() => {
                    setRelation(value);
                  }}
                >
                  {t(RELATION_LABEL[value])}
                </button>
              ))}
            </div>

            <div className={styles.controlsSpacer} />

            <div className={styles.sort}>
              <Select
                label={t('sort.label')}
                size="sm"
                value={sort}
                onChange={(event) => {
                  // The allowlist is closed and the options are drawn from it, so the cast is
                  // over a value this component itself produced.
                  setSort(event.target.value as ProjectSort);
                }}
              >
                {SORTS.map((value) => (
                  <option key={value} value={value}>
                    {t(SORT_LABEL[value])}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {list.isError ? (
            <div className={styles.state}>
              <InlineError variant="block">{t('error.load')}</InlineError>
              <Button
                onClick={() => {
                  void list.refetch();
                }}
              >
                {t('action.retry')}
              </Button>
            </div>
          ) : list.isPending ? (
            /* The grid's own shape, held until the cards land. Four, because that is about one
               row on a common width and a row count is a guess about content. */
            <div
              className={styles.grid}
              role="status"
              aria-busy="true"
              aria-label={t('status.loading')}
            >
              {PLACEHOLDER_CARDS.map((key) => (
                <div key={key} className={[styles.card, styles.cardSkeleton].join(' ')} aria-hidden>
                  <div className={styles.thumb}>
                    <Skeleton width="100%" height="100%" />
                  </div>
                  <div className={styles.cardName}>
                    <Skeleton width="70%" height={12} />
                  </div>
                  <div className={styles.cardCaption}>
                    <Skeleton width="46%" height={10} />
                  </div>
                  <div className={styles.cardSpacer} />
                  <div className={styles.metaStrip}>
                    <div className={styles.metaRow}>
                      <Skeleton width="30%" height={10} />
                    </div>
                    <div className={styles.metaRow}>
                      <Skeleton width="55%" height={10} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : projects.length === 0 ? (
            // `list`: the dashed box is drawn where the card grid would be.
            <EmptyState
              variant="list"
              // TODO(-014): placeholder sentence in the brief's voice; wave 3 writes the real one.
              explains={t('empty.body')}
              title={filtered ? t('emptyFiltered.title') : t('empty.title')}
              body={filtered ? t('emptyFiltered.body') : undefined}
              action={
                filtered ? (
                  <Button
                    onClick={() => {
                      setStatus('all');
                      setRelation('all');
                    }}
                  >
                    {t('action.clearFilters')}
                  </Button>
                ) : (
                  <Button
                    ref={createFirst}
                    variant="primary"
                    icon={<Plus size={16} strokeWidth={1.5} aria-hidden />}
                    onClick={() => {
                      setDrawerOpen(true);
                    }}
                  >
                    {t('action.createFirst')}
                  </Button>
                )
              }
            />
          ) : (
            <>
              <div className={styles.grid}>
                {projects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
              {list.hasNextPage ? (
                <div ref={setSentinel} className={styles.more}>
                  <Button
                    loading={list.isFetchingNextPage}
                    onClick={() => {
                      void list.fetchNextPage();
                    }}
                  >
                    {t('action.loadMore')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </Screen>

      {drawerOpen ? (
        <NewProjectDrawer
          onClose={() => {
            setDrawerOpen(false);
          }}
          // Straight onto the drawings screen, ready for the first PDF (`KLQ-20261023-011`).
          // A project is created flat — its root node and nothing under it — and its structure
          // is offered when the drawings need it, never asked on arrival.
          onCreated={(project) => {
            void navigate({
              to: '/projects/$projectId/drawings',
              params: { projectId: project.id },
            });
          }}
        />
      ) : null}
    </AppChrome>
  );
}
